import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { useRecordTable } from '../../../hooks/useRecordTable';

import TableHeader from '../advancedRecord/TableHeader';
import TableBody from '../advancedRecord/TableBody';
import FilterBar, { FilterConfig } from './FilterBar';
import AdvancedTablePagination from './AdvancedTablePagination';

import SvgIcon from '../../SvgIcon/SvgIcon';

import { IDropdownSupplier } from '../../../types/supplier/supplierInterfaces';
import { IDocumentIdentificationTypeDropdown } from '../../../store/slices/documentIdentificationType/documentIdentificationTypeSlice';
import { CustomColumn } from '../../../types/customColumn';

interface TopLeftAction {
  label: string;
  onClick: () => void;
  count?: number;
}

export interface TopRightAction {
  label: string;
  onClick?: () => void;
  href?: string;
}

interface AdvancedRecordTableProps<T extends object> {
  columns: CustomColumn<T>[];
  data: T[];

  pageSize?: number;
  noDataMessage?: string;
  showFooter?: boolean;

  filtersConfig?: FilterConfig[];

  documentTypes?: IDocumentIdentificationTypeDropdown[];
  suppliers?: IDropdownSupplier[];

  onFilterChange?: (filters: Record<string, string>) => void;

  getActions?: (row: any) => any[];

  onPageSizeChange: (size: number) => void;

  onPageChange: (page: number) => void;

  paginator?: any;
  url?: string;

  /**
   * 🔥 opcional:
   * no limitar ancho por defecto
   */
  maxWidth?: string;

  topLeftActions?: TopLeftAction[];
  topRightAction?: TopRightAction;

  onResetFilters?: () => void;
}

const AdvancedRecordTable = <T extends object>({
  columns,
  data,

  pageSize,
  noDataMessage = 'No hay registros disponibles.',

  filtersConfig,
  onFilterChange,

  getActions,

  onPageSizeChange,
  onPageChange,

  paginator,
  url,

  /**
   * 🔥 mejor default:
   * tabla libre para crecer
   */
  maxWidth = '',

  topLeftActions,
  topRightAction,

  onResetFilters,
}: AdvancedRecordTableProps<T>) => {
  const table = useRecordTable<T>({
    columns,
    data,
    pageSize,
  });

  return (
    <div
      className={clsx(
        `
          w-full
          rounded-sm
          border
          border-stroke
          bg-white
          shadow-default
          dark:border-strokedark
          dark:bg-boxdark
        `,
        maxWidth
      )}
    >
      {/* =========================
          TOP ACTIONS
      ========================== */}
      {(topLeftActions?.length || topRightAction) && (
        <div
          className="
            flex
            flex-col
            gap-3
            px-4
            pt-4
            sm:flex-row
            sm:items-center
            sm:justify-between
          "
        >
          {/* LEFT ACTIONS */}
          <div className="flex flex-wrap gap-2">
            {topLeftActions?.map((btn, idx) => (
              <button
                key={idx}
                type="button"
                onClick={btn.onClick}
                className="
                    flex
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded
                    bg-primary
                    px-4.5
                    py-2.5
                    text-sm
                    font-medium
                    text-white
                    hover:bg-opacity-90
                    sm:w-auto
                  "
              >
                <span className="text-center leading-tight">{btn.label}</span>

                {btn.count !== undefined && (
                  <span
                    className="
                        ml-2
                        rounded-full
                        bg-white
                        bg-opacity-20
                        px-2
                        py-0.5
                        text-xs
                        font-semibold
                      "
                  >
                    {btn.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* RIGHT ACTION */}
          {topRightAction && (
            <>
              {topRightAction.onClick ? (
                <button
                  type="button"
                  onClick={topRightAction.onClick}
                  className="
                    flex
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded
                    bg-primary
                    px-4.5
                    py-2.5
                    text-sm
                    font-medium
                    text-white
                    hover:bg-opacity-90
                    sm:w-auto
                  "
                >
                  <SvgIcon name="plus" className="h-4 w-4" />

                  {topRightAction.label}
                </button>
              ) : (
                <Link
                  to={topRightAction.href!}
                  className="
                    flex
                    w-full
                    items-center
                    justify-center
                    gap-2
                    rounded
                    bg-primary
                    px-4.5
                    py-2.5
                    text-sm
                    font-medium
                    text-white
                    hover:bg-opacity-90
                    sm:w-auto
                  "
                >
                  <SvgIcon name="plus" />

                  {topRightAction.label}
                </Link>
              )}
            </>
          )}
        </div>
      )}

      {/* =========================
          FILTERS
      ========================== */}
      {filtersConfig && (
        <FilterBar
          filtersConfig={filtersConfig}
          onFilterChange={onFilterChange}
          onResetFilters={onResetFilters}
        />
      )}

      {/* =========================
          TABLE
      ========================== */}
      <section
        className="
          data-table-common
          data-table-two
          py-4
        "
      >
        <div
          className="
            w-full
            overflow-x-auto
            p-2
          "
        >
          <table
            {...table.getTableProps()}
            className="
              datatable-table
              w-full
              min-w-max
              table-auto
              border-collapse
              whitespace-nowrap
            "
          >
            <TableHeader table={table} showFilters={false} />

            <TableBody
              table={table}
              getActions={getActions}
              noDataMessage={noDataMessage}
            />
          </table>
        </div>

        {/* =========================
            PAGINATION
        ========================== */}
        <AdvancedTablePagination
          data={data}
          url={url}
          paginator={paginator}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          onPageChange={onPageChange}
        />
      </section>
    </div>
  );
};

export default AdvancedRecordTable;
